import type { GamePhase, PlayerRole, TeamState, TeamTactics } from '@/store/useGameStore';
import { playingRoles, type GameSystem } from '@/utils/gameSystem';

/**
 * Rôle tactique réel d'un joueur, indépendamment de sa position visuelle courante :
 * libéro d'abord, puis rôle à sa vraie position sur le terrain, puis rôle nominal.
 * Utilisé pour colorer les pastilles ET les chips de sélection de façon cohérente.
 * With a game system, the role played this rally (VOL-162), as on the touch courts.
 */
export function resolveTacticalRole(team: TeamState, name: string, gameSystem?: GameSystem): PlayerRole | '' {
  if (name === team.libero) return 'Lib';
  const idx = team.court.indexOf(name);
  if (idx !== -1 && idx < 6 && gameSystem) {
    const played = playingRoles(team, gameSystem)[idx];
    if (played) return played;
  }
  if (idx !== -1 && team.courtRoles[idx]) return team.courtRoles[idx];
  return team.playerRoles[name] ?? '';
}

/**
 * Phases pendant lesquelles l'équipe qui ne sert pas est encore **placée pour la
 * réception**. Dès la balle réceptionnée, elle rejoint ses positions de jeu :
 * rester en formation de réception tout le point était faux (les joueurs ne
 * bougeaient plus). Mêmes phases que la non-permutation de `computeVisualCourt`,
 * pour que placement et permutation restent cohérents.
 */
const RECEPTION_FORMATION_PHASES: ReadonlySet<GamePhase> = new Set<GamePhase>([
  'Serve',
  'Reception',
]);

/**
 * Le côté `side` est-il en formation de réception ? Piloté par le SERVICE (l'équipe
 * qui ne sert pas réceptionne) et borné aux phases d'engagement.
 */
export function isInReceptionFormation(
  side: 'us' | 'them',
  servingSide: 'us' | 'them',
  gamePhase: GamePhase,
): boolean {
  return servingSide !== side && RECEPTION_FORMATION_PHASES.has(gamePhase);
}

const BACK_ROW_INDICES = [0, 4, 5] as const;

/**
 * Court index (back row) of the player the libero stands in for, or -1.
 *
 * The players chosen in the tactics (`liberoReplaces`, roster ids) win: a
 * position role alone let the libero take the opposite's place whenever she
 * carried the `Central` role (VOL-161). Without a chosen player in the back row
 * (none configured, or one replaced by a substitute), the back-row position
 * holding the `Central` role is used, as before.
 */
/**
 * The two players chosen in « Le libéro remplace » are both on court but not across
 * from each other: some rotations put them together in the back row, where the
 * libero stands in for one only (zone 6, then 5, then 1).
 */
export function liberoChoicesNotOpposite(
  team: Pick<TeamState, 'court' | 'roster'>,
  liberoReplaces: readonly string[] | undefined,
): boolean {
  const chosen = new Set(liberoReplaces ?? []);
  if (chosen.size < 2) return false;
  const idOf = (name: string) => team.roster.find((p) => p.name === name)?.id ?? '';
  const slots = team.court.slice(0, 6).flatMap((name, i) => (chosen.has(idOf(name)) ? [i] : []));
  return slots.length === 2 && Math.abs(slots[0] - slots[1]) !== 3;
}

export function liberoTargetIndex(team: TeamState, tactics: TeamTactics | undefined): number {
  const chosen = new Set(tactics?.liberoReplaces ?? []);
  if (chosen.size > 0) {
    const idOf = (name: string | undefined) => team.roster.find((p) => p.name === name)?.id;
    const byChoice = BACK_ROW_INDICES.filter((i) => chosen.has(idOf(team.court[i]) ?? ''));
    if (byChoice.length > 0) return byChoice[byChoice.length - 1];
  }
  // The role played under the system: with three middles the one playing opposite
  // keeps his place, with one middle the player across from him gives it up.
  const roles = playingRoles({ ...team, courtRoles: team.courtRoles ?? [] }, tactics?.gameSystem);
  const centrals = BACK_ROW_INDICES.filter((i) => roles[i] === 'Central');
  return centrals.length > 0 ? centrals[centrals.length - 1] : -1;
}

interface VisualCourtInput {
  team: TeamState;
  statTarget: 'us' | 'them';
  servingSide: 'us' | 'them';
  gamePhase: GamePhase;
  isP1SwapActive: boolean;
  teamTactics: TeamTactics;
}

export function computeVisualCourt({
  team,
  statTarget,
  servingSide,
  gamePhase,
  isP1SwapActive,
  teamTactics,
}: VisualCourtInput): string[] {
  // Placed by the role each player plays this rally under the team's system
  // (opposite R4 in a 5-1 with three outside hitters, second setter in a 4-2 / 6-2).
  const roles = playingRoles({ ...team, courtRoles: team.courtRoles ?? [] }, teamTactics?.gameSystem);
  const visualRaw = team.court.slice(0, 6);
  if (visualRaw.length < 6 || roles.length < 6) {
    return [...visualRaw, ...Array(Math.max(0, 6 - visualRaw.length)).fill('')].slice(0, 6);
  }

  const tactics: TeamTactics = teamTactics ?? {
    p1AttackZone2: 'R4',
    liberoDefenseZone: 5,
    centralDefenseZone: 6,
  };

  const visual = [...visualRaw];
  const centralIndex = liberoTargetIndex(team, tactics);
  // `statTarget` is the side being rendered. The 'Serve' phase alone does not
  // mean this side serves: in two-team capture it is the other side's serve,
  // and the libero must already be in for the player in zone 1.
  const teamServes = statTarget === 'them' ? servingSide === 'them' : servingSide === 'us';
  const centralReplaced = centralIndex !== -1 && team.libero !== null && !(teamServes && centralIndex === 0);
  if (centralReplaced && team.libero) visual[centralIndex] = team.libero;

  const isPermuted =
    !['Serve', 'Reception'].includes(gamePhase) &&
    gamePhase !== 'PointEnd' &&
    gamePhase !== 'SetEnd' &&
    gamePhase !== 'SetSetup';

  if (!isPermuted) return visual;

  const frontRow = [1, 2, 3].map((i) => ({ i, name: visual[i], role: roles[i] }));
  // The libero keeps her own role whatever the position she took over: a chosen
  // player may hold an R4 or Pointu position role.
  const backRow = [0, 5, 4].map((i) => ({
    i,
    name: visual[i],
    role: centralReplaced && i === centralIndex ? ('Lib' as const) : roles[i],
  }));
  const newVisual = [...visual];

  const f_residue = [...frontRow];
  let p_z4: (typeof frontRow)[number] | undefined;
  let p_z3: (typeof frontRow)[number] | undefined;
  let p_z2: (typeof frontRow)[number] | undefined;

  const idxMid = f_residue.findIndex((p) => p.role === 'Central');
  if (idxMid !== -1) {
    p_z3 = f_residue[idxMid];
    f_residue.splice(idxMid, 1);
  }

  // `isP1SwapActive` est fourni POUR L'ÉQUIPE RENDUE (sa rotation P1, l'autre équipe
  // au service), avec sa propre tactique. Il était réservé à notre équipe : la
  // rotation P1 de l'adversaire était ignorée.
  // Échange visuel R4↔Pointu/Passeur en P1 : si la tactique place le R4 en zone 2
  // (p1AttackZone2 === 'R4'), le R4 reste en zone 2 et le Pointu en zone 4 sur tout
  // le sideout (réception + phases de jeu). Sans ce swap, la permutation par défaut
  // ramène le Pointu en zone 2 (proche du passeur) et le R4 en zone 4.
  const effectiveP1Swap = isP1SwapActive && tactics.p1AttackZone2 === 'R4';
  const idxSet = f_residue.findIndex((p) => p.role === 'Passeur' || p.role === 'Pointu');
  if (idxSet !== -1) {
    const player = f_residue[idxSet];
    if (effectiveP1Swap) p_z4 = player;
    else p_z2 = player;
    f_residue.splice(idxSet, 1);
  }
  const idxR4 = f_residue.findIndex((p) => p.role === 'R4');
  if (idxR4 !== -1) {
    const player = f_residue[idxR4];
    if (effectiveP1Swap) p_z2 = player;
    else p_z4 = player;
    f_residue.splice(idxR4, 1);
  }
  if (!p_z4 && f_residue.length) p_z4 = f_residue.shift();
  if (!p_z3 && f_residue.length) p_z3 = f_residue.shift();
  if (!p_z2 && f_residue.length) p_z2 = f_residue.shift();
  if (p_z4) newVisual[3] = p_z4.name;
  if (p_z3) newVisual[2] = p_z3.name;
  if (p_z2) newVisual[1] = p_z2.name;

  const b_residue = [...backRow];
  let p_z5: (typeof backRow)[number] | undefined;
  let p_z6: (typeof backRow)[number] | undefined;
  let p_z1: (typeof backRow)[number] | undefined;
  // Zones de défense de l'équipe rendue : `tactics` est désormais fourni par côté
  // (chaque équipe a sa configuration en saisie deux équipes). Les valeurs par
  // défaut valent 5/6, soit exactement l'ancien comportement figé de l'adversaire.
  const { liberoDefenseZone, centralDefenseZone } = tactics;
  const defZone = centralReplaced ? liberoDefenseZone : centralDefenseZone;

  const idxBR4 = b_residue.findIndex((p) => p.role === 'R4');
  if (idxBR4 !== -1) {
    if (defZone === 6) p_z5 = b_residue[idxBR4];
    else p_z6 = b_residue[idxBR4];
    b_residue.splice(idxBR4, 1);
  }
  const idxLibOnly = b_residue.findIndex((p) => p.role === 'Lib');
  const idxBLib = idxLibOnly !== -1 ? idxLibOnly : b_residue.findIndex((p) => p.role === 'Central');
  if (idxBLib !== -1) {
    if (defZone === 6) p_z6 = b_residue[idxBLib];
    else p_z5 = b_residue[idxBLib];
    b_residue.splice(idxBLib, 1);
  }
  const idxBSet = b_residue.findIndex((p) => p.role === 'Passeur' || p.role === 'Pointu');
  if (idxBSet !== -1) {
    p_z1 = b_residue[idxBSet];
    b_residue.splice(idxBSet, 1);
  }
  if (!p_z6 && b_residue.length) p_z6 = b_residue.shift();
  if (!p_z5 && b_residue.length) p_z5 = b_residue.shift();
  if (!p_z1 && b_residue.length) p_z1 = b_residue.shift();
  if (p_z6) newVisual[5] = p_z6.name;
  if (p_z5) newVisual[4] = p_z5.name;
  if (p_z1) newVisual[0] = p_z1.name;

  return newVisual;
}

interface SuggestedPlayersInput {
  team: TeamState;
  gamePhase: GamePhase;
  lastTouchPlayer: string | null;
  visualCourt: string[];
  gameSystem?: GameSystem;
}

export function computeSuggestedPlayers({
  team,
  gamePhase,
  lastTouchPlayer,
  visualCourt,
  gameSystem,
}: SuggestedPlayersInput): string[] {
  const suggestions: string[] = [];
  const roles = playingRoles(team, gameSystem);

  const tacticalRoleOf = (name: string): PlayerRole | '' => {
    const courtIdx = team.court.indexOf(name);
    if (courtIdx !== -1) return roles[courtIdx];
    if (name === team.libero) return 'Lib';
    return team.playerRoles[name] ?? '';
  };
  // In a 5-1 with three outside hitters, the one playing opposite still receives.
  const receives = (name: string, role: PlayerRole | ''): boolean =>
    role === 'Lib' || role === 'R4' || name === team.libero ||
    (gameSystem === '5-1-3R4' && team.playerRoles[name] === 'R4');

  visualCourt.forEach((p) => {
    const role = tacticalRoleOf(p);
    let isSuggested = false;

    if (gamePhase === 'Serve') {
      if (p === visualCourt[0]) isSuggested = true;
    } else if (gamePhase === 'Reception') {
      if (receives(p, role)) isSuggested = true;
    } else if (gamePhase === 'Set') {
      const settersOnCourt = visualCourt.filter((nm) => tacticalRoleOf(nm) === 'Passeur');
      const hasMultipleSetters = settersOnCourt.length > 1;
      if (role === 'Passeur' && p !== lastTouchPlayer) {
        if (hasMultipleSetters) {
          const vi = visualCourt.indexOf(p);
          if ([0, 4, 5].includes(vi)) isSuggested = true;
        } else {
          isSuggested = true;
        }
      }
      if (lastTouchPlayer && tacticalRoleOf(lastTouchPlayer) === 'Passeur') {
        if (role === 'Lib' || p === team.libero) isSuggested = true;
      } else if (lastTouchPlayer && lastTouchPlayer !== p && role === 'Passeur') {
        isSuggested = true;
      }
    } else if (gamePhase === 'Attack') {
      if (role !== 'Lib' && p !== team.libero && role !== 'Passeur') isSuggested = true;
    } else if (gamePhase === 'Block') {
      const vi = visualCourt.indexOf(p);
      if ([1, 2, 3].includes(vi)) isSuggested = true;
    } else if (gamePhase === 'Dig') {
      isSuggested = true;
    }

    if (isSuggested) suggestions.push(p);
  });
  return suggestions;
}
